import { useProfileDraft } from "@/context/ProfileDraftContext";
import type { SectionType } from "@profolio/validators";
import { SECTION_METADATA, createDefaultSectionContent } from "@/components/content/types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RotateCcw, Save, Loader2 } from "lucide-react";

// Section Editors
import { AboutEditor } from "@/components/sections/AboutEditor";
import { PublicationsEditor } from "@/components/sections/PublicationsEditor";
import { ResearchEditor } from "@/components/sections/ResearchEditor";
import { ExperienceEditor } from "@/components/sections/ExperienceEditor";
import { ContactEditor } from "@/components/sections/ContactEditor";
import { TeachingEditor } from "@/components/sections/TeachingEditor";
import { ProjectsEditor } from "@/components/sections/ProjectsEditor";
import { SkillsEditor } from "@/components/sections/SkillsEditor";
import { DownloadsEditor } from "@/components/sections/DownloadsEditor";
import { MediaEditor } from "@/components/sections/MediaEditor";

// Other Views
import { HomeStudio } from "@/components/home/HomeStudio";
import { DesignStudio } from "@/components/design/DesignStudio";
import { PagesManagement } from "@/components/pages/PagesManagement";
import { SeoPreviewPanel } from "@/components/preview/SeoPreviewPanel";
import { DisplayOptionsEditor } from "@/components/design/DisplayOptionsEditor";

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(objB, key)) return false;
    if (!deepEqual(objA[key], objB[key])) return false;
  }
  return true;
}

export function MainInspector() {
  const {
    draft,
    serverSnapshot,
    lastSavedDraft,
    dispatch,
    selectedNode,
    inspectorTab,
    setInspectorTab,
    userTier,
    onUpgradeClick,
    transientSectionOverrides,
    setTransientSectionOverrides,
    flushAutosave,
    handleSaveDraft,
    isSavingDraft,
  } = useProfileDraft();

  if (!selectedNode) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-muted-foreground text-sm h-full">
        Select a node from the outline.
      </div>
    );
  }

  if (selectedNode.kind === "overview") {
    return <div className="flex-1 overflow-y-auto h-full"><HomeStudio /></div>;
  }

  if (selectedNode.kind === "pages") {
    return <div className="flex-1 overflow-y-auto h-full"><PagesManagement /></div>;
  }

  if (selectedNode.kind === "publish") {
    return (
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 h-full">
        <div className="max-w-4xl mx-auto">
          <SeoPreviewPanel
            profile={draft?.profile || {}}
            userTier={userTier}
            onUpgradeClick={() => onUpgradeClick("seo_overrides")}
            onUpdateSeo={(seoOverrides) =>
              dispatch({ type: "UPDATE_PROFILE", payload: { seoOverrides } })
            }
          />
        </div>
      </div>
    );
  }

  if (
    selectedNode.kind === "siteStyle" ||
    selectedNode.kind === "header" ||
    selectedNode.kind === "effects" ||
    selectedNode.kind === "footer" ||
    selectedNode.kind === "advanced"
  ) {
    return <div className="flex-1 overflow-hidden h-full"><DesignStudio /></div>;
  }

  if (selectedNode.kind === "section") {
    const activeSection = draft?.sections.find(s => 
      (selectedNode.clientKey && s.clientKey === selectedNode.clientKey) || 
      s.id === selectedNode.sectionId
    );
    if (!activeSection) {
       return <div className="flex-1 p-8 text-muted-foreground text-sm">Section not found.</div>;
    }
    const meta = SECTION_METADATA[activeSection.type as SectionType];

    const baselineDraft = lastSavedDraft || serverSnapshot;
    const savedSection = baselineDraft?.sections.find(s => 
      s.id === activeSection.id || (activeSection.clientKey && s.clientKey === activeSection.clientKey)
    );

    const defaultContent = createDefaultSectionContent(activeSection.type as SectionType);
    const baselineContent = savedSection ? savedSection.content : defaultContent;

    const hasContentChanges = !deepEqual(activeSection.content, baselineContent);
    const hasDesignChanges = !!transientSectionOverrides[activeSection.id];

    const handleDiscardContent = () => {
      if (!activeSection) return;
      dispatch({ 
        type: "UPDATE_SECTION_CONTENT", 
        payload: { sectionId: activeSection.id, content: baselineContent as any } 
      });
      toast.success("Content changes discarded.");
    };

    const handleApplyContent = async () => {
      try {
        await handleSaveDraft();
      } catch {
        // handleSaveDraft handles error notification
      }
    };

    const handleDiscardDesign = () => {
      if (!activeSection) return;
      setTransientSectionOverrides((prev: any) => {
        const next = { ...prev };
        delete next[activeSection.id];
        return next;
      });
      toast.success("Design changes discarded.");
    };

    const handleApplyDesign = async () => {
      if (!activeSection) return;
      const override = transientSectionOverrides[activeSection.id];
      if (override) {
        dispatch({
          type: "UPDATE_SECTION_CONTENT",
          payload: {
            sectionId: activeSection.id,
            content: { ...(activeSection.content as object), ...override },
          },
        });
        setTransientSectionOverrides((prev: any) => {
          const next = { ...prev };
          delete next[activeSection.id];
          return next;
        });
        await flushAutosave();
        toast.success("Design changes applied!");
      }
    };

    const handleSectionContentChange = (
      sectionId: string,
      content: Record<string, unknown>
    ) => {
      dispatch({ type: "UPDATE_SECTION_CONTENT", payload: { sectionId, content } });
    };

    return (
      <div className="flex-1 flex flex-col h-full bg-background min-w-0">
        {/* Inspector Header with Tabs */}
        <div className="flex flex-col border-b border-border bg-card shrink-0">
          <div className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <h2 className="text-lg font-bold text-foreground truncate">
                {meta?.label || activeSection.type}
              </h2>
              {inspectorTab === "content" && hasContentChanges && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved
                </span>
              )}
              {inspectorTab === "design" && hasDesignChanges && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Unsaved
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {inspectorTab === "content" && hasContentChanges && (
                <div className="flex items-center gap-2 animate-in fade-in duration-150">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isSavingDraft}
                    onClick={handleDiscardContent}
                    className="h-8 px-2.5 text-xs font-medium cursor-pointer"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    <span>Discard</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isSavingDraft}
                    onClick={handleApplyContent}
                    className="h-8 px-3 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-all cursor-pointer shadow-xs gap-1.5"
                  >
                    {isSavingDraft ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    <span>Apply Changes</span>
                  </Button>
                </div>
              )}
              {inspectorTab === "design" && hasDesignChanges && (
                <div className="flex items-center gap-2 animate-in fade-in duration-150">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={isSavingDraft}
                    onClick={handleDiscardDesign}
                    className="h-8 px-2.5 text-xs font-medium cursor-pointer"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    <span>Discard</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isSavingDraft}
                    onClick={handleApplyDesign}
                    className="h-8 px-3 text-xs font-semibold bg-primary hover:bg-primary/90 text-primary-foreground transition-all cursor-pointer shadow-xs gap-1.5"
                  >
                    {isSavingDraft ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Save className="h-3.5 w-3.5" />
                    )}
                    <span>Apply Changes</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="px-4 flex gap-4">
            <button
              onClick={() => setInspectorTab("content")}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 cursor-pointer ${
                inspectorTab === "content" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>Content</span>
              {hasContentChanges && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              )}
            </button>
            <button
              onClick={() => setInspectorTab("design")}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 cursor-pointer ${
                inspectorTab === "design" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span>Design</span>
              {hasDesignChanges && (
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 max-w-3xl w-full mx-auto">
          {inspectorTab === "content" ? (
             <div className="space-y-6">
                {activeSection.type === "about" && (
                  <AboutEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "publications" && (
                  <PublicationsEditor
                    content={activeSection.content as Record<string, unknown>}
                    userTier={userTier}
                    onUpgradeClick={() => onUpgradeClick("publications_import")}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "research" && (
                  <ResearchEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "experience" && (
                  <ExperienceEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "contact" && (
                  <ContactEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}


                {activeSection.type === "teaching" && (
                  <TeachingEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "projects" && (
                  <ProjectsEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "skills" && (
                  <SkillsEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "downloads" && (
                  <DownloadsEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}

                {activeSection.type === "media" && (
                  <MediaEditor
                    content={activeSection.content as Record<string, unknown>}
                    onChange={(content) =>
                      handleSectionContentChange(activeSection.id, content)
                    }
                  />
                )}
             </div>
          ) : (
            <div className="space-y-6">
               <DisplayOptionsEditor sectionId={activeSection.id} sectionType={activeSection.type as SectionType} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
